import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import * as styles from '../FilterBar.style.css'

export interface SortableConfigItem {
  id: string
  label: string
  checked: boolean
  disabled?: boolean
}

export interface SortableConfigListProps {
  items: SortableConfigItem[]
  onCheckedChange: (id: string, checked: boolean) => void
  onOrderChange: (orderedIds: string[]) => void
  checkboxClassName?: string
  labelClassName?: string
}

function SortableItem({
  item,
  onCheckedChange,
  checkboxClassName,
  labelClassName,
}: {
  item: SortableConfigItem
  onCheckedChange: (id: string, checked: boolean) => void
  checkboxClassName?: string
  labelClassName?: string
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`${styles.filterConfigModalListItem} ${styles.sortableItem} ${isDragging ? styles.sortableItemDragging : ''}`}
    >
      <div className={styles.filterConfigModalLabel}>
        <button
          type="button"
          className={styles.dragHandle}
          aria-label={`Reorder ${item.label}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>
        <label className={styles.sortableItemLabel}>
          <input
            type="checkbox"
            className={checkboxClassName ?? styles.filterConfigModalCheckbox}
            checked={item.checked}
            disabled={item.disabled}
            onChange={(e) => onCheckedChange(item.id, e.target.checked)}
          />
          <span className={labelClassName ?? undefined}>{item.label}</span>
        </label>
      </div>
    </li>
  )
}

export function SortableConfigList({
  items,
  onCheckedChange,
  onOrderChange,
  checkboxClassName,
  labelClassName,
}: SortableConfigListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((i) => i.id === active.id)
      const newIndex = items.findIndex((i) => i.id === over.id)
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(items, oldIndex, newIndex)
        onOrderChange(reordered.map((i) => i.id))
      }
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <ul className={styles.filterConfigModalList}>
          {items.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              onCheckedChange={onCheckedChange}
              checkboxClassName={checkboxClassName}
              labelClassName={labelClassName}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  )
}
